/**
 * Copilot Orb — persistent surface
 * ═══════════════════════════════════════════════════════════════
 * Phase 2 · task 2.15 (surface 1 of 5)
 *
 * The always-present entry point. It is deliberately the quietest surface:
 * it sits still, shows which copilot is bound to the current route, and does
 * nothing until asked — except when a trigger rule fires, when it pulses once
 * rather than opening anything.
 *
 * Rendered only when the resolved copilot declares `surfaces.orb`.
 */
import { useState } from 'react';
import { Sparkles, X, ShieldCheck, Circle } from 'lucide-react';

export default function CopilotOrb({
  agent,
  pulse = false,
  recording = false,
  onOpenSidebar,
  onOpenPalette,
}) {
  const [expanded, setExpanded] = useState(false);

  if (!agent) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9990] flex flex-col items-end gap-2">
      {expanded && (
        <div className="w-64 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <Sparkles size={14} className="text-blue-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-gray-800 truncate">{agent.name}</p>
              {/* Which copilot answered and why — the same information the
                  resolve endpoint returns, surfaced so a route bound to the
                  wrong agent is visible rather than mysterious. */}
              {agent.matchedPattern && (
                <p className="text-[11px] text-gray-400 truncate">bound to {agent.matchedPattern}</p>
              )}
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="text-gray-400 hover:text-gray-600 shrink-0"
              aria-label="Close copilot menu"
            >
              <X size={13} />
            </button>
          </div>

          <div className="p-1.5">
            <button
              onClick={() => { setExpanded(false); onOpenSidebar?.(); }}
              className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-gray-700 hover:bg-gray-50"
            >
              Ask about this page
            </button>
            <button
              onClick={() => { setExpanded(false); onOpenPalette?.(); }}
              className="w-full text-left px-3 py-2 rounded-lg text-[12px] text-gray-700 hover:bg-gray-50 flex items-center justify-between"
            >
              <span>Command palette</span>
              <kbd className="text-[11px] text-gray-400 border border-gray-200 rounded px-1">⌘K</kbd>
            </button>
          </div>

          {/* Task 2.17 — the recording indicator. A person should be able to
              see that their actions are being observed without going looking
              for a settings page. */}
          {recording && (
            <div className="flex items-center gap-1.5 px-4 py-2 border-t border-gray-100 bg-amber-50">
              <Circle size={7} className="text-amber-500 fill-amber-500 shrink-0" />
              <span className="text-[11px] text-amber-700">
                Recording your actions on this page to offer help
              </span>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        aria-label={`Open ${agent.name}`}
        className={`relative h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600
                    text-white shadow-lg flex items-center justify-center
                    transition-transform hover:scale-105 active:scale-95
                    ${pulse ? 'animate-pulse ring-4 ring-blue-300/50' : ''}`}
      >
        <Sparkles size={18} />
        {recording && (
          <span
            className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-400 border-2 border-white"
            title="Your actions are being recorded"
          />
        )}
        {agent.streamOptedOut && (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-white border border-gray-200 flex items-center justify-center"
            title="You have opted out of action recording"
          >
            <ShieldCheck size={8} className="text-emerald-600" />
          </span>
        )}
      </button>
    </div>
  );
}
